import React from 'react';

import { hasPrestations } from '@front/common/src/utils/basket';
import { BasketContent, User } from '@front/common/src/types/commons';
import colors from '@front/common/src/utils/colors';
import { formatPercentage, formatPrice, formatPricePerMonth } from '@front/common/src/utils/formatting';
import { getCurrentCountry } from '@front/common/src/utils/country';

import { MEMBERSHIP_CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';
import useMembershipOptIn from '@front/customer/src/hooks/membership/use-membership-opt-in';

import { translate } from '@front/i18n';

import Text from '@front/web-ui/src/components/base-text/variants/text';
import Title from '@front/web-ui/src/components/base-text/variants/title';
import TextButton from '@front/web-ui/src/components/button/text-button';

import MembershipCardContentOptin from './membership-card-content-optin';

const t = translate('basket.membership.card');

type MembershipCardContentProps = {
  isMembershipRequested: boolean;
  basketContent: BasketContent;
  user?: User;
  getMoreInfoPress: () => void;
  reduction: number;
};

const MembershipCardContent = ({
  isMembershipRequested,
  basketContent,
  user,
  getMoreInfoPress,
  reduction,
}: MembershipCardContentProps) => {
  const hasPrestationsSelected = hasPrestations(basketContent);
  const doesAlreadyHaveMembership = !!user?.membership;
  const membershipConfig = MEMBERSHIP_CONFIG_BY_COUNTRY[getCurrentCountry()];
  const membershipPrice = membershipConfig.monthlyPrice;
  const membershipReductionsPercentage = membershipConfig.wellnessReductionsPercentage;
  const { isLegallyCompliantMembershipOptIn } = useMembershipOptIn({ user });

  if (isLegallyCompliantMembershipOptIn && !doesAlreadyHaveMembership) {
    return <MembershipCardContentOptin getMoreInfoPress={getMoreInfoPress} />;
  }

  if (!hasPrestationsSelected) {
    if (doesAlreadyHaveMembership) {
      return (
        <span>
          <Text.SMMedium>{`${t('.reductions.happy')}`}</Text.SMMedium>
          <Text.SMMedium>{`${t(
            '.money_saved_without_services.with_user_who_already_has_membership.text_1',
          )} `}</Text.SMMedium>
          <Title color={colors.pink120}>{`${formatPercentage(membershipReductionsPercentage)} `}</Title>
          <Text.SMMedium>
            {t('.money_saved_without_services.with_user_who_already_has_membership.text_2')}
          </Text.SMMedium>
        </span>
      );
    }

    return isMembershipRequested ? (
      <span>
        <Text.SMMedium>{`${t('.reductions.happy')} `}</Text.SMMedium>
        <Text.SMMedium>{`${t('.money_saved_without_services.with_requested_membership.text_1')} `}</Text.SMMedium>
        <Title color={colors.pink120}>{`${formatPercentage(membershipReductionsPercentage)} `}</Title>
        <Text.SMMedium>{`${t('.money_saved_without_services.with_requested_membership.text_2')} `}</Text.SMMedium>
        <TextButton size='sm' onClick={getMoreInfoPress}>
          {t('.more_info_link.the_wecasa_club')}
        </TextButton>
        <Text.SMMedium>
          {t('.money_saved_without_services.with_requested_membership.text_3', {
            pricePerMonth: formatPricePerMonth(membershipPrice),
          })}
        </Text.SMMedium>
      </span>
    ) : (
      <span>
        <Text.SMMedium>{`${t('.money_saved_without_services.without_membership.text_1')} `}</Text.SMMedium>
        <Title color={colors.pink120}>{`${formatPercentage(membershipReductionsPercentage)} `}</Title>
        <Text.SMMedium>{`${t('.money_saved_without_services.without_membership.text_2')} `}</Text.SMMedium>
        <TextButton size='sm' onClick={getMoreInfoPress}>
          {t('.more_info_link.the_wecasa_club')}
        </TextButton>
        <Text.SMMedium>
          {t('.money_saved_without_services.without_membership.text_3', {
            pricePerMonth: formatPricePerMonth(membershipPrice),
          })}
        </Text.SMMedium>
      </span>
    );
  }

  if (doesAlreadyHaveMembership) {
    return (
      <span>
        <Text.SMMedium>{`${t('.reductions.happy')} `}</Text.SMMedium>
        <Text.SMMedium>{`${t(
          '.money_saved_with_services.with_user_who_already_has_membership.text_1',
        )} `}</Text.SMMedium>
        <Title color={colors.pink120}>{`${formatPrice(reduction)} `}</Title>
        <Text.SMMedium>{t('.money_saved_with_services.with_user_who_already_has_membership.text_2')}</Text.SMMedium>
      </span>
    );
  }

  return isMembershipRequested ? (
    <span>
      <Text.SMMedium>{`${t('.reductions.happy')} `}</Text.SMMedium>
      <Text.SMMedium>{`${t('.money_saved_with_services.with_requested_membership.text_1')} `}</Text.SMMedium>
      <Title color={colors.pink120}>{`${formatPrice(reduction)} `}</Title>
      <Text.SMMedium>{`${t('.money_saved_with_services.with_requested_membership.text_2')} `}</Text.SMMedium>
      <TextButton size='sm' onClick={getMoreInfoPress}>
        {t('.more_info_link.the_wecasa_club')}
      </TextButton>
      <Text.SMMedium>
        {t('.money_saved_with_services.with_requested_membership.text_3', {
          pricePerMonth: formatPricePerMonth(membershipPrice),
        })}
      </Text.SMMedium>
    </span>
  ) : (
    <span>
      <Text.SMMedium>{`${t('.money_saved_with_services.without_membership.text_1')} `}</Text.SMMedium>
      <Title color={colors.pink120}>{`${formatPrice(reduction)} `}</Title>
      <Text.SMMedium>{`${t('.money_saved_with_services.without_membership.text_2')} `}</Text.SMMedium>
      <TextButton size='sm' onClick={getMoreInfoPress}>
        {t('.more_info_link.the_wecasa_club')}
      </TextButton>
      <Text.SMMedium>
        {t('.money_saved_with_services.without_membership.text_3', {
          pricePerMonth: formatPricePerMonth(membershipPrice),
        })}
      </Text.SMMedium>
    </span>
  );
};

export default MembershipCardContent;
